"use client"

import { useCallback, useEffect, useState } from "react"
import Image from "next/image"
import type { HomeHeroSlide, HomeHeroSlideTone } from "@lib/content/home-intro"
import { toDisplayImageUrl } from "@lib/util/encode-remote-image-url"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

export type HomeHeroSlideView = HomeHeroSlide & {
  imageUrl: string | null
}

type HomeHeroProps = {
  slides: HomeHeroSlideView[]
}

const AUTOPLAY_MS = 6000

const TONE_STYLES: Record<
  HomeHeroSlideTone,
  { background: string; overlay: string }
> = {
  hotel: {
    background:
      "radial-gradient(ellipse 70% 80% at 85% 40%, rgba(0,111,255,0.35), transparent 55%), linear-gradient(135deg, #0b1a33 0%, #132848 45%, #0f172a 100%)",
    overlay: "from-[#0b1a33]/95 via-[#0b1a33]/70 to-transparent",
  },
  office: {
    background:
      "radial-gradient(ellipse 60% 70% at 90% 30%, rgba(92,100,112,0.35), transparent 50%), linear-gradient(135deg, #0b0f16 0%, #1a2332 50%, #111827 100%)",
    overlay: "from-[#0b0f16]/95 via-[#0b0f16]/70 to-transparent",
  },
  network: {
    background:
      "radial-gradient(ellipse 65% 75% at 80% 50%, rgba(0,111,255,0.45), transparent 55%), linear-gradient(135deg, #06101f 0%, #0c2748 48%, #062033 100%)",
    overlay: "from-[#06101f]/95 via-[#06101f]/65 to-transparent",
  },
  software: {
    background:
      "radial-gradient(ellipse 70% 80% at 88% 35%, rgba(14,116,144,0.4), transparent 55%), linear-gradient(135deg, #0a1628 0%, #123047 50%, #0b0f16 100%)",
    overlay: "from-[#0a1628]/95 via-[#0a1628]/70 to-transparent",
  },
}

function HeroCta({
  cta,
  variant,
}: {
  cta: HomeHeroSlide["primaryCta"]
  variant: "primary" | "secondary"
}) {
  const className =
    variant === "primary"
      ? "inline-flex items-center justify-center border border-white bg-white px-5 py-3 text-sm font-semibold tracking-wide text-vx-ink transition-colors hover:bg-vx-accent hover:border-vx-accent hover:text-white"
      : "inline-flex items-center justify-center border border-white/50 bg-transparent px-5 py-3 text-sm font-semibold tracking-wide text-white transition-colors hover:border-white hover:bg-white/10"

  if (cta.external) {
    return (
      <a
        href={cta.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {cta.label}
      </a>
    )
  }

  return (
    <LocalizedClientLink href={cta.href} className={className}>
      {cta.label}
    </LocalizedClientLink>
  )
}

export default function HomeHero({ slides }: HomeHeroProps) {
  const safeSlides = slides.length > 0 ? slides : []
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [reduceMotion, setReduceMotion] = useState(false)

  const count = safeSlides.length
  const active = safeSlides[index] ?? safeSlides[0]

  const go = useCallback(
    (next: number) => {
      if (count < 1) return
      setIndex(((next % count) + count) % count)
    },
    [count]
  )

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const apply = () => setReduceMotion(mq.matches)
    apply()
    mq.addEventListener("change", apply)
    return () => mq.removeEventListener("change", apply)
  }, [])

  useEffect(() => {
    if (count < 2 || paused || reduceMotion) return
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % count)
    }, AUTOPLAY_MS)
    return () => window.clearInterval(timer)
  }, [count, paused, reduceMotion, index])

  if (!active) {
    return null
  }

  return (
    <section
      className="relative overflow-hidden border-b border-vx-line"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setPaused(false)
        }
      }}
      aria-roledescription="carousel"
      aria-label="โปรโมทหน้าแรก"
    >
      <div className="relative min-h-[360px] small:min-h-[480px] medium:min-h-[520px]">
        {safeSlides.map((slide, slideIndex) => {
          const isActive = slideIndex === index
          const src = toDisplayImageUrl(slide.imageUrl)
          const slideTone = TONE_STYLES[slide.tone]

          return (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-500 ease-out ${
                isActive ? "opacity-100 z-[1]" : "opacity-0 z-0 pointer-events-none"
              }`}
              role="group"
              aria-roledescription="slide"
              aria-label={`${slideIndex + 1} จาก ${count}`}
              aria-hidden={!isActive}
            >
              <div
                className="absolute inset-0"
                style={{ background: slideTone.background }}
                aria-hidden
              />
              {src ? (
                <div className="pointer-events-none absolute inset-y-0 right-0 w-full small:w-[58%]">
                  <Image
                    src={src}
                    alt=""
                    fill
                    priority={slideIndex === 0}
                    sizes="(max-width: 1024px) 100vw, 58vw"
                    className="object-contain object-right-bottom p-6 small:p-10 opacity-90"
                  />
                  <div
                    className={`absolute inset-0 bg-gradient-to-r ${slideTone.overlay} small:via-[#0b0f16]/40`}
                    aria-hidden
                  />
                </div>
              ) : null}

              <div className="relative z-[2] content-container flex min-h-[360px] flex-col justify-center py-12 small:min-h-[480px] medium:min-h-[520px]">
                <div className="max-w-xl text-white">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
                    {slide.eyebrow}
                  </p>
                  <h1 className="mt-3 text-3xl font-semibold leading-tight tracking-tight small:text-4xl medium:text-5xl">
                    {slide.title}
                  </h1>
                  <p className="mt-4 max-w-md text-sm leading-6 text-white/80 small:text-base small:leading-7">
                    {slide.body}
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <HeroCta cta={slide.primaryCta} variant="primary" />
                    {slide.secondaryCta ? (
                      <HeroCta cta={slide.secondaryCta} variant="secondary" />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          )
        })}

        {count > 1 ? (
          <>
            <button
              type="button"
              aria-label="สไลด์ก่อนหน้า"
              onClick={() => go(index - 1)}
              className="absolute left-3 top-1/2 z-[3] hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/25 text-white backdrop-blur-sm transition-colors hover:bg-black/45 small:flex"
            >
              <span aria-hidden className="text-xl leading-none">
                ‹
              </span>
            </button>
            <button
              type="button"
              aria-label="สไลด์ถัดไป"
              onClick={() => go(index + 1)}
              className="absolute right-3 top-1/2 z-[3] hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/25 text-white backdrop-blur-sm transition-colors hover:bg-black/45 small:flex"
            >
              <span aria-hidden className="text-xl leading-none">
                ›
              </span>
            </button>

            <div className="absolute bottom-5 left-0 right-0 z-[3] flex justify-center gap-2 px-4">
              {safeSlides.map((slide, slideIndex) => {
                const isActive = slideIndex === index
                return (
                  <button
                    key={slide.id}
                    type="button"
                    aria-label={`ไปสไลด์ ${slideIndex + 1}`}
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => go(slideIndex)}
                    className="h-1.5 w-10 overflow-hidden rounded-full bg-white/25"
                  >
                    <span
                      key={
                        isActive
                          ? `progress-${slideIndex}-${index}-${paused ? "p" : "r"}`
                          : `idle-${slideIndex}`
                      }
                      className={`block h-full origin-left rounded-full bg-white ${
                        isActive
                          ? reduceMotion || paused
                            ? "w-full"
                            : "w-full animate-hero-progress"
                          : "w-0"
                      }`}
                    />
                  </button>
                )
              })}
            </div>
          </>
        ) : null}
      </div>
    </section>
  )
}
